import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { SignatureType, SubmissionStatus } from "generated-graphql/graphql";
import { SubmissionReviewState } from "hooks/submission/reviewStates";
import { useMemo } from "react";
import { Controller, Control, FieldErrors } from "react-hook-form";

interface ReviewFormValues {
  reviewFinished: boolean;
  rejectionComment: string;
}

export interface ReviewLabelContext {
  isReviewerASigner: boolean;
  isWetInkSigner: boolean;
  isStaffInitialReview: boolean;
  hasCustomerReviewers: boolean;
  hasOtherPendingReviewers: boolean;
  hasSigner: boolean;
}

export function getApproveLabel(ctx: ReviewLabelContext): string {
  if (ctx.isReviewerASigner) {
    if (ctx.isWetInkSigner) {
      return "Ready for wet ink signature (will be mailed)";
    }
    return "Done signing, ready for submission";
  }
  if (ctx.isStaffInitialReview && ctx.hasCustomerReviewers) {
    return "Approved, ready for customer review";
  }
  if (ctx.hasOtherPendingReviewers) {
    return "Approved, ready for next reviewer";
  }
  if (ctx.hasSigner) {
    return "Approved, ready for signature";
  }
  return "Approved, ready for submission";
}

export function getReviewHelperText(ctx: ReviewLabelContext & { reviewFinished: boolean }): string {
  if (!ctx.reviewFinished) {
    return "We will send this back for edits based on your comments.";
  }
  if (ctx.isStaffInitialReview && ctx.hasCustomerReviewers) {
    return "We will approve this review and send it to the customer reviewer(s).";
  }
  if (ctx.hasOtherPendingReviewers) {
    return "We will approve this review and send it to the next reviewer.";
  }
  if (ctx.hasSigner) {
    return "We will approve this review and move it forward for signature.";
  }
  return "We will approve this review and move it forward for submission.";
}

interface SubmissionDisplayReviewOrSignDocumentProps {
  isReviewerASigner: boolean;
  isCurrentUserStaff: boolean;
  hasSigner: boolean;
  hasCustomerReviewers: boolean;
  hasOtherPendingReviewers: boolean;
  reviewState: SubmissionReviewState;
  reviewFinished: boolean;
  rejectionComment: string;
  submissionDocumentReviewId: string | null;
  submissionStatus?: SubmissionStatus | null;
  signatureType?: SignatureType | null;
  signingCompleted?: boolean;
  isMutating: boolean;
  control: Control<ReviewFormValues>;
  errors: FieldErrors<ReviewFormValues>;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

export function SubmissionDisplayReviewOrSignDocument({
  isReviewerASigner,
  isCurrentUserStaff,
  hasSigner,
  hasCustomerReviewers,
  hasOtherPendingReviewers,
  reviewState,
  reviewFinished,
  rejectionComment,
  submissionDocumentReviewId,
  submissionStatus,
  signatureType,
  signingCompleted = false,
  isMutating,
  control,
  errors,
  onSubmit,
}: SubmissionDisplayReviewOrSignDocumentProps) {
  const isSignerWaitingForPrereqs = useMemo(
    () => reviewState === SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS,
    [reviewState]
  );

  const isWetInkSigner = isReviewerASigner && signatureType === SignatureType.WetInk;
  const isDigitalSigner = isReviewerASigner && signatureType === SignatureType.Digital;

  let formLegend = "Review submission";
  if (isDigitalSigner) {
    formLegend = "Review document";
  } else if (isReviewerASigner) {
    formLegend = "Review document (wet ink signature)";
  }

  const isStaffInitialReview =
    isCurrentUserStaff && submissionStatus === SubmissionStatus.EncampInitialReview;

  const labelContext: ReviewLabelContext = useMemo(
    () => ({
      isReviewerASigner,
      isWetInkSigner,
      isStaffInitialReview,
      hasCustomerReviewers,
      hasOtherPendingReviewers,
      hasSigner,
    }),
    [
      isReviewerASigner,
      isWetInkSigner,
      isStaffInitialReview,
      hasCustomerReviewers,
      hasOtherPendingReviewers,
      hasSigner,
    ]
  );

  const approveLabel = useMemo(() => getApproveLabel(labelContext), [labelContext]);

  const isReviewStatusAllowed = useMemo(
    () =>
      submissionStatus === SubmissionStatus.EncampInitialReview ||
      submissionStatus === SubmissionStatus.InReview,
    [submissionStatus]
  );

  const disallowedReviewStatusTooltip =
    "Review actions are only available when the submission is in Encamp Initial Review or In Review.";

  const isRejectionCommentMissing = useMemo(
    () => !isCurrentUserStaff && !reviewFinished && rejectionComment.trim().length === 0,
    [isCurrentUserStaff, reviewFinished, rejectionComment]
  );

  const isDigitalSignerAwaitingSignature = isDigitalSigner && reviewFinished && !signingCompleted;

  let reviewButtonTooltip = "";
  if (isDigitalSignerAwaitingSignature) {
    reviewButtonTooltip = "Please sign the document before completing the review.";
  } else if (!isReviewStatusAllowed) {
    reviewButtonTooltip = disallowedReviewStatusTooltip;
  }

  const isSubmitDisabled = useMemo(() => {
    if (isSignerWaitingForPrereqs) {
      return true;
    }
    if (!isReviewStatusAllowed) {
      return true;
    }
    if (isRejectionCommentMissing) {
      return true;
    }
    if (isDigitalSignerAwaitingSignature) {
      return true;
    }
    return !submissionDocumentReviewId || isMutating;
  }, [
    isSignerWaitingForPrereqs,
    isReviewStatusAllowed,
    isRejectionCommentMissing,
    isDigitalSignerAwaitingSignature,
    submissionDocumentReviewId,
    isMutating,
  ]);

  // Grid placement is the caller's: the read-only view pairs this with the
  // comment stream in a two-column container, the stepper renders it full width.
  return (
    <Stack spacing={2} sx={{ height: "100%" }}>
      <Box
        component="form"
        onSubmit={onSubmit}
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        <FormControl component="fieldset" disabled={isSignerWaitingForPrereqs} sx={{ mb: 3 }}>
          <FormLabel component="legend">{formLegend}</FormLabel>
          <Controller
            name="reviewFinished"
            control={control}
            render={({ field }) =>
              isDigitalSigner ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Your signature serves as approval. Check the box below only if edits are needed.
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!field.value}
                        onChange={(event) => field.onChange(!event.target.checked)}
                      />
                    }
                    label={`🔄 Needs edits${isCurrentUserStaff ? "" : " (rejection comment required)"}`}
                  />
                </>
              ) : (
                <RadioGroup
                  {...field}
                  value={field.value ? "true" : "false"}
                  onChange={(event) => field.onChange(event.target.value === "true")}
                >
                  <FormControlLabel value="true" control={<Radio />} label={`✅ ${approveLabel}`} />
                  <FormControlLabel
                    value="false"
                    control={<Radio />}
                    label={`🔄 Needs edits${isCurrentUserStaff ? "" : " (rejection comment required)"}`}
                  />
                </RadioGroup>
              )
            }
          />
        </FormControl>

        {!reviewFinished && (
          <Controller
            name="rejectionComment"
            control={control}
            rules={{
              validate: (value, formValues) => {
                if (isCurrentUserStaff || formValues.reviewFinished) {
                  return true;
                }
                return value.trim().length > 0
                  ? true
                  : "A rejection comment is required when selecting Needs edits.";
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                multiline
                minRows={3}
                fullWidth
                label={isCurrentUserStaff ? "Rejection comment (optional)" : "Rejection comment"}
                InputLabelProps={{ shrink: true }}
                placeholder="Describe what needs to be corrected before resubmission."
                error={Boolean(errors.rejectionComment)}
                helperText={errors.rejectionComment?.message}
                sx={{ mb: 3 }}
              />
            )}
          />
        )}

        {submissionDocumentReviewId && !isReviewerASigner && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {getReviewHelperText({ ...labelContext, reviewFinished })}
          </Typography>
        )}

        {!submissionDocumentReviewId && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Review actions will be available once a document review is assigned.
          </Typography>
        )}

        <Box
          sx={{
            mt: "auto",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Tooltip title={reviewButtonTooltip}>
            <span>
              <Button type="submit" variant="contained" disabled={isSubmitDisabled}>
                Complete My Review
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Stack>
  );
}
